<template>
  <PortalPageV2
    :general="general"
    :reports="reports"
    :services="services"
    :articles="articles"
    :is-loading="isLoading"
    :videos="videos"
    :events="events"
  />
</template>

<script setup lang="ts">
import PortalPageV2 from '~/components/shared/price-assessments/PortalPageV2.vue'
import { usePortal } from '~/composables/usePortal'
import { useSeo } from '~/composables/useSeo'
import newTechnologyPortalData from '~/constants/portal/new-technology'
import { getServices } from '~/constants/services'
import {
  NEW_TECHNOLOGY,
  SILICON_ANODE,
  SOLID_STATE,
} from '~/constants/services/constants'
import { useAuthStore } from '~/stores/auth'

type TServices = {
  checkboxes: string[]
}

const general = {
  ...NEW_TECHNOLOGY,
  ...newTechnologyPortalData,
  title: NEW_TECHNOLOGY.name,
  icon: NEW_TECHNOLOGY.iconColor,
  categoryAnalysis: NEW_TECHNOLOGY.slug,
}

const categories = [SOLID_STATE.slug, SILICON_ANODE.slug]
const services: TServices = getServices([general.slug])

definePageMeta({ layout: 'services' })
const titlePage = `${NEW_TECHNOLOGY.name} Portal`
const autStore = useAuthStore()
const { getReports, getArticles, getVideos, getEvents } = usePortal()
const { getSeoInfo } = useSeo()
const seo = getSeoInfo({
  title: titlePage,
  description:
    'Discover essential market insights and weekly reports on emerging battery technologies that are reshaping the energy landscape and driving strategic decisions.',
})

const isAuthenticated = computed(() => autStore.authenticated)

const { data, status } = useAsyncData(
  `${general.slug}-portal`,
  async () => {
    const [reports, articles, videos] = await Promise.all([
      getReports(general.slug),
      getArticles(general.slug, categories),
      getVideos({ slug: general.slug, categories }),
    ])
    return {
      articles,
      reports,
      videos,
    }
  },
  {
    watch: [isAuthenticated],
    default: () => ({
      articles: [],
      reports: [],
      videos: [],
    }),
  },
)

const { data: events } = useAsyncData(
  `${general.slug}-portal-events`,
  async () => {
    return await getEvents()
  },
  {
    default: () => [],
  },
)

const articles = computed(() => data.value?.articles || [])
const reports = computed(() => data.value?.reports || [])
const videos = computed(() => data.value?.videos || [])
const isLoading = computed(() => status.value !== 'success')

useHead({
  ...seo,
  title: `Benchmark Mineral Intelligence: ${titlePage}`,
})
</script>
